import { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { usePopperTooltip, PopperOptions, TriggerType } from 'react-popper-tooltip';
import 'react-popper-tooltip/dist/styles.css';

const StyledContainer = styled.div<{
  white?: boolean;
  red?: boolean;
  black?: boolean;
  grey?: boolean;
  delayHide?: number;
  maxWidth?: string;
}>`
  border-radius: 3px;
  max-width: ${props => (props.maxWidth ? props.maxWidth : '250px')};
  padding: 4px 4px;
  font-family: ${props => props.theme.fonts.montserratMedium};
  font-size: 1rem;
  line-height: 1.2rem;
  font-weight: normal;
  margin: 0.5rem 0;
  ${props => {
    if (props.red) {
      return css`
        &.tooltip-container {
          background: ${props.theme.colors.tooltip.red};
          border-color: ${props.theme.colors.tooltip.red};
          color: ${props.theme.colors.white};

          .tooltip-arrow::before {
            border-color: ${props.theme.colors.tooltip.red} transparent transparent transparent;
          }

          .tooltip-arrow::after {
            border-color: ${props.theme.colors.tooltip.red} transparent transparent transparent;
          }
        }
      `;
    } else if (props.white) {
      return css`
        &.tooltip-container {
          background: ${props.theme.colors.tooltip.white};
          border-color: ${props.theme.colors.tooltip.white};
          color: ${props.theme.colors.siteBlack};

          .tooltip-arrow::before {
            border-color: ${props.theme.colors.tooltip.white} transparent transparent transparent;
          }

          .tooltip-arrow::after {
            border-color: ${props.theme.colors.tooltip.white} transparent transparent transparent;
          }
        }
      `;
    } else if (props.black) {
      return css`
        &.tooltip-container {
          background: ${props.theme.colors.siteBlack};
          border-color: ${props.theme.colors.siteBlack};
          color: ${props.theme.colors.white};

          .tooltip-arrow::before {
            border-color: ${props.theme.colors.siteBlack} transparent transparent transparent;
          }

          .tooltip-arrow::after {
            border-color: ${props.theme.colors.siteBlack} transparent transparent transparent;
          }
        }
      `;
    } else if (props.grey) {
      return css`
        &.tooltip-container {
          background: ${props.theme.colors.siteGrey};
          border-color: ${props.theme.colors.siteGrey};
          color: ${props.theme.colors.siteBlack};

          .tooltip-arrow::before {
            border-color: ${props.theme.colors.siteGrey} transparent transparent transparent;
          }

          .tooltip-arrow::after {
            border-color: ${props.theme.colors.siteGrey} transparent transparent transparent;
          }
        }
      `;
    } else {
      return css`
        &.tooltip-container {
          background: ${props.theme.colors.siteNavy};
          border-color: ${props.theme.colors.siteNavy};
          color: ${props.theme.colors.white};

          .tooltip-arrow::before {
            border-color: ${props.theme.colors.siteNavy} transparent transparent transparent;
          }

          .tooltip-arrow::after {
            border-color: ${props.theme.colors.siteNavy} transparent transparent transparent;
          }
        }
      `;
    }
  }}
`;

const TooltipArrow = styled.div``;
const TooltipTrigger = styled.button`
  padding: 0;
  margin: 0;
`;

type TooltipProps = {
  className?: string;
  hideArrow?: boolean;
  maxWidth?: string;
  trigger?: TriggerType;
  red?: boolean;
  interactive?: boolean;
  delayHide?: number;
  white?: boolean;
  black?: boolean;
  grey?: boolean;
  children?: ReactNode;
  tooltip: string | ReactNode;
  tooltipBold?: string | ReactNode;
};

export const Tooltip = ({
  className,
  interactive,
  children,
  maxWidth,
  tooltip,
  tooltipBold,
  hideArrow,
  delayHide,
  red,
  white,
  black,
  grey,
  trigger,
  placement,
  ...restProps
}: TooltipProps & PopperOptions) => {
  const { getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef, visible } =
    usePopperTooltip(
      {
        // closeOnOutsideClick,
        // closeOnTriggerHidden,
        // defaultVisible,
        delayHide,
        // delayShow,
        // followCursor,
        interactive,
        // mutationObserverOptions,
        // offset,
        // onVisibleChange,
        placement: placement ?? 'top',
        trigger: trigger || ['click', 'hover'],
        ...restProps
      },
      {
        strategy: 'fixed'
      }
    );

  return (
    <span className={className}>
      <TooltipTrigger className={`${className} trigger`} type="button" ref={setTriggerRef}>
        {children}
      </TooltipTrigger>
      {visible && (
        <StyledContainer
          maxWidth={maxWidth}
          red={red}
          white={white}
          black={black}
          grey={grey}
          delayHide={delayHide}
          ref={setTooltipRef}
          {...getTooltipProps({ className: 'tooltip-container' })}
        >
          {!hideArrow && <TooltipArrow {...getArrowProps({ className: 'tooltip-arrow' })} />}
          <div className="tooltip-body" style={{ fontWeight: 'bold' }}>
            {tooltipBold}
          </div>
          <div className="tooltip-body">{tooltip}</div>
        </StyledContainer>
      )}
    </span>
  );
};

export default Tooltip;
